
































































































import rest from "@/rest";
import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { ElementSaveStateVm, ExChoiceList } from "@/interfaces/ExDataSet";
import MarkdownViewer from "../Shared/MarkdownViewer.vue";
import ImageWithDnD from "../Shared/ImageWithDnD.vue";

const globals = namespace("globals");
const quizplayerstore = namespace("quizplayerstore");

@Component({
  components: {
    ImageWithDnD,
    MarkdownViewer
  }
})
export default class CompFlipCardList extends Vue {
  @Prop() guid!: string;
  @Prop() choiceList!: ExChoiceList;
  @Prop() finalized!: boolean;
  @Prop({ required: true }) private src!: string;
  @Prop() compSettings!: string;

  @Prop({ default: false }) private preventJumping!: boolean; // Used by settings component

  @quizplayerstore.Action setElementState!: (saveState: ElementSaveStateVm) => void;
  @quizplayerstore.Action getElementState!: (settings: string) => Promise<string | null>;

  enableCarousel = true;
  activeSlide = 0;
  flipStates: Record<number, boolean> = {};
  isMobile = false;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchDeltaX = 0;
  private touchDeltaY = 0;
  private touchActive = false;

  // Data properties
  private settings: any = null;

  // Lifecycle hooks
  async mounted(): Promise<void> {
    // Parse component settings
    if (this.compSettings) {
      this.settings = JSON.parse(this.compSettings);
    }

    // Try to load existing state for this element
    if (this.choiceList?.items) {
      let saveState = await this.getElementState(this.guid);
      if (saveState) {
        // Apply the loaded settings
        const selectedIds = JSON.parse(saveState);
        this.choiceList.items?.forEach(item => {
          item.selected = selectedIds.includes(item.id);
        });
      }
    }

    // mobile detection for swipe handling
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile as any);
    // global keyboard navigation so focus loss doesn't break keys
    // window.addEventListener('keydown', this.onGlobalKey as any);
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.updateIsMobile as any);
    // window.removeEventListener('keydown', this.onGlobalKey as any);
  }

  toggleFlip(index: number) {
    const newState = !this.flipStates[index];
    this.$set(this.flipStates, index, newState);
    // mark item as viewed/selected when flipped to back
    if (newState && this.choiceList && this.choiceList.items && this.choiceList.items[index]) {
      const item = this.choiceList.items[index] as any;
      this.$set(item, 'selected', true);
      this.updateElementState();
    }
  }

  private updateElementState() {
    if (this.finalized || !this.choiceList || !this.getPreventJumping) return;

      // Create an array with all ids of selected choices
      const selectedIds = this.choiceList.items?.filter(item => item.selected).map(item => item.id) ?? [];

      this.setElementState( {
          guid: this.guid,
          settings: JSON.stringify(selectedIds)
        } as ElementSaveStateVm
      );

    // Enable "Weiter" button if preventJumping is active
    const listItemCount = this.choiceList.items?.length ?? 0;
    const selectedCount = this.choiceList.items?.filter(item => item.selected).length ?? 0;
    if (selectedCount == listItemCount) {
      this.$store.commit("quizplayerstore/setChoiceState",
        { id: this.choiceList.id, questionId: this.choiceList.questionId, isRight: true, selectionLable: "" }
      );
    }
  }

  isFlipped(index: number): boolean {
    return !!this.flipStates[index];
  }

  @Watch('activeSlide')
  onActiveSlideChanged(newIndex: number, oldIndex: number) {
    if (newIndex === oldIndex) return;

    const items = this.choiceList?.items;
    if (!items || items.length === 0) return;

    // If preventJumping is disabled, accept navigation and focus new slide
    if (!this.getPreventJumping) {
      this.focusCarouselItem(newIndex);
      return;
    }

    // moving forward: require current item already selected (flipped)
    if (newIndex > oldIndex) {
      const current = items[oldIndex] as any;
      const allowed = !!(current && current.selected);
      if (!allowed) {
        this.$nextTick(() => { this.activeSlide = oldIndex; });
      } else {
        this.focusCarouselItem(newIndex);
      }
      return;
    }

    // moving backward: allow only to items that have been selected (flipped)
    if (newIndex < oldIndex) {
      const target = items[newIndex] as any;
      const allowed = !!(target && target.selected);
      if (!allowed) {
        this.$nextTick(() => { this.activeSlide = oldIndex; });
      } else {
        this.focusCarouselItem(newIndex);
      }
      return;
    }
  }

  get cardBackground(): string {
    const vuetify: any = (this as any).$vuetify;
    const dark = vuetify && vuetify.theme && vuetify.theme.dark;
    return dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)';
  }

  get cardStyle() {
    const accent = (this.$store && (this.$store.state as any)?.ux && (this.$store.state as any).ux.colorAccent) || 'rgba(25,118,210,0.9)';
    return {
      "background": this.cardBackground,
      "height": '100%',
      "display": 'flex',
      "flexDirection": 'column',
      "minHeight": '0',
      "--focus-accent": accent
    } as any;
  }

  get showNextTooltip(): boolean {
    if (!this.getPreventJumping) return false;
    const items = this.choiceList?.items;
    if (!items || items.length === 0) return false;
    const current = (items as any)[this.activeSlide];
    return !!(current && !current.selected);
  }

  get canGoNext(): boolean {
    const items = this.choiceList?.items ?? [];
    if (items.length === 0) return false;
    if (!this.getPreventJumping) return this.activeSlide < items.length - 1;
    // when preventJumping, moving next requires current selected
    const current = items[this.activeSlide] as any;
    return !!(current && current.selected) && this.activeSlide < items.length - 1;
  }

  get canGoPrev(): boolean {
    const items = this.choiceList?.items ?? [];
    if (items.length === 0) return false;
    if (!this.getPreventJumping) return this.activeSlide > 0;
    // when preventJumping, can go back only to already selected items
    return this.activeSlide > 0 && !!((this.choiceList.items as any)[this.activeSlide - 1]?.selected);
  }

  goNext() {
    if (!this.canGoNext) return;
    this.activeSlide = Math.min((this.choiceList?.items?.length ?? 1) - 1, this.activeSlide + 1);
  }

  goPrev() {
    if (!this.canGoPrev) return;
    this.activeSlide = Math.max(0, this.activeSlide - 1);
  }

  goFirst() {
    if (!this.canGoPrev) return;
    this.activeSlide = 0;
  }

  goLast() {
    const items = this.choiceList?.items ?? [];
    const lastIndex = (items.length === 0) ? 0 : items.length - 1;
    if (!this.getPreventJumping) {
      if (!this.canGoNext) return;
      this.activeSlide = lastIndex;
      return;
    }

    // When preventJumping is enabled, advance as far as allowed by selected flags.
    // You may move from index i to i+1 only if items[i].selected === true.
    if (items.length === 0) return;
    let idx = this.activeSlide;
    // If current is not selected, we cannot move forward at all.
    while (idx < lastIndex && (items[idx] as any).selected) {
      idx++;
    }
    this.activeSlide = idx;
  }

  get getPreventJumping() {
    if (this.settings?.preventJumping)
      return this.settings.preventJumping;

    return this.preventJumping;
  }

  get title() {
    return this.settings?.title || "";
  }

  get srStatusMessage(): string {
    const items = this.choiceList?.items;
    if (!items || items.length === 0) return "";

    const totalCount = items.length;
    const viewedCount = items.filter(i => i.selected).length;

    if (this.getPreventJumping && viewedCount === totalCount) {
      return `Alle ${totalCount} Elemente wurden angesehen. Sie können jetzt fortfahren.`;
    }

    const item = items[this.activeSlide];
    if (!item) return "";

    const flipped = this.isFlipped(this.activeSlide);
    const side = flipped ? 'Rückseite' : 'Vorderseite';
    const sideText = flipped
      ? ((item as any).resolutionText || 'Kein Lösungstext vorhanden.')
      : ((item as any).text || 'Kein Beschreibungstext vorhanden.');
    let msg = `Flip Card Element ${this.activeSlide + 1} von ${totalCount}, ${side}: ${sideText}`;
    if (this.getPreventJumping && viewedCount < totalCount) {
      msg += ` ${viewedCount} von ${totalCount} Elementen angesehen.`;
    }
    return msg;
  }

  handleKeyNavigation(event: KeyboardEvent, currentIndex: number): void {
    const items = this.choiceList?.items;
    if (!items || items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.goNext();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.goPrev();
        break;
      case 'Home':
        event.preventDefault();
        this.goFirst();
        break;
      case 'End':
        event.preventDefault();
        this.goLast();
        break;
      default:
        return;
    }
  }

  // onGlobalKey(event: KeyboardEvent): void {
  //   // Ignore when typing into inputs or contenteditable
  //   const target = event.target as HTMLElement | null;
  //   if (target) {
  //     const tag = (target.tagName || '').toUpperCase();
  //     if (tag === 'INPUT' || tag === 'TEXTAREA' || (target as HTMLElement).isContentEditable) return;
  //   }

  //   switch (event.key) {
  //     case 'ArrowRight':
  //     case 'ArrowDown':
  //       if (this.canGoNext) {
  //         event.preventDefault();
  //         this.goNext();
  //       }
  //       break;
  //     case 'ArrowLeft':
  //     case 'ArrowUp':
  //       if (this.canGoPrev) {
  //         event.preventDefault();
  //         this.goPrev();
  //       }
  //       break;
  //     case 'Home':
  //       event.preventDefault();
  //       this.goFirst();
  //       break;
  //     case 'End':
  //       event.preventDefault();
  //       this.goLast();
  //       break;
  //     default:
  //       return;
  //   }
  // }

  focusCarouselItem(index: number): void {
    // Access the card directly via $refs to avoid fragile CSS class queries.
    // Poll until the element is visible (offsetParent !== null) after Vuetify's transition.
    let attempts = 0;
    const tryFocus = () => {
      if (!this.$refs.flipCards) return;
      const cards = this.$refs.flipCards as Vue[];
      const card = cards[index];
      if (!card) return;
      const el = (card as any).$el as HTMLElement;
      if (el && el.offsetParent !== null) {
        el.focus();
        return;
      }
      if (++attempts < 12) setTimeout(tryFocus, 50);
    };
    setTimeout(tryFocus, 50);
  }

  updateIsMobile(): void {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth <= 720;
  }

  onTouchStart(e: TouchEvent): void {
    if (!this.isMobile) return;
    if (!e.touches || e.touches.length === 0) return;
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
    this.touchActive = true;
  }

  onTouchMove(e: TouchEvent): void {
    if (!this.isMobile) return;
    if (!this.touchActive || !e.touches || e.touches.length === 0) return;
    this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
    this.touchDeltaY = e.touches[0].clientY - this.touchStartY;
    // intercept horizontal swipes before Vuetify carousel can handle them
    if (Math.abs(this.touchDeltaX) > Math.abs(this.touchDeltaY) && Math.abs(this.touchDeltaX) > 10) {
      e.stopPropagation();
    }
  }

  onTouchEnd(e: TouchEvent): void {
    if (!this.isMobile) return;
    if (!this.touchActive) return;
    const dx = this.touchDeltaX;
    const dy = this.touchDeltaY;
    this.touchActive = false;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;

    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const threshold = 40; // minimum px to consider a swipe

    if (absX > absY && absX > threshold) {
      e.stopPropagation();
      if (dx < 0) this.goNext();
      else this.goPrev();
    }
  }
}
